import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Card,
    CardContent,
    CircularProgress,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import Logger from "../../../services/Logger";
import { CsvDownloadButton, csvNumber, downloadCsv } from "./adminCsvExport";
import StatsSummaryTile from "./StatsSummaryTile";

dayjs.extend(isoWeek);

const RANGE_OPTIONS = [
    { value: "7d", label: "Last 7 days", days: 7 },
    { value: "1m", label: "Last month", days: 30 },
    { value: "3m", label: "Last 3 months", days: 90 },
    { value: "12m", label: "Last 12 months", days: 365 },
];

const DEFAULT_RANGE = "1m";

/** Calendar windows for puzzle-size and most-played charts. Week = Monday–today. */
const BREAKDOWN_PERIOD_OPTIONS = [
    { value: "all", label: "All" },
    { value: "month", label: "This month" },
    { value: "week", label: "This week" },
];

const DEFAULT_BREAKDOWN_PERIOD = "month";

function loadStoredBool(key, fallback) {
    if (!key) return fallback;
    try {
        const stored = window.localStorage.getItem(key);
        if (stored === "true") return true;
        if (stored === "false") return false;
    } catch {
        /* private mode */
    }
    return fallback;
}

function persistStoredBool(key, value) {
    if (!key) return;
    try {
        window.localStorage.setItem(key, value ? "true" : "false");
    } catch {
        /* private mode */
    }
}

const rangeDays = (value) => RANGE_OPTIONS.find((option) => option.value === value)?.days ?? 30;

function breakdownPeriodBounds(period) {
    const end = dayjs();
    if (period === "week") {
        return { start: end.startOf("isoWeek"), end };
    }
    if (period === "month") {
        return { start: end.startOf("month"), end };
    }
    // Backend treats missing/0 start as last month, so send a real far-past timestamp.
    return { start: dayjs("2000-01-01"), end };
}

function breakdownPeriodStorageKey(rangeStorageKey) {
    return rangeStorageKey ? `${rangeStorageKey}.breakdown` : "";
}

const formatNumber = (value) => (Number.isFinite(Number(value)) ? Number(value).toLocaleString() : "—");

const formatDecimal = (value) => (Number.isFinite(Number(value)) ? Number(value).toFixed(1) : "—");

const formatPercent = (value) => `${formatDecimal(value)}%`;

function MostPlayedTable({ rows, startRank = 1 }) {
    if (!rows.length) return null;
    return (
        <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 320 }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 600, width: 56 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Puzzle</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Plays</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Signed-in</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Guests</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Completed</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, index) => (
                        <TableRow key={row.storyId}>
                            <TableCell sx={{ fontWeight: 800, fontSize: "1.75rem", lineHeight: 1, color: "#3BA666" }}>
                                {startRank + index}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                                    {row.imageUrl ? (
                                        <Box
                                            component="img"
                                            src={row.imageUrl}
                                            alt=""
                                            sx={{
                                                width: 88,
                                                height: 88,
                                                objectFit: "cover",
                                                borderRadius: 1,
                                                flexShrink: 0,
                                                bgcolor: "action.hover",
                                            }}
                                        />
                                    ) : null}
                                    {row.title || row.storyId}
                                </Box>
                            </TableCell>
                            <TableCell align="right">{formatNumber(row.plays)}</TableCell>
                            <TableCell align="right">{formatNumber(row.players)}</TableCell>
                            <TableCell align="right">{formatNumber(row.guests)}</TableCell>
                            <TableCell align="right">{formatNumber(row.completedPlays)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

const formatDuration = (seconds) => {
    const totalSeconds = Number(seconds);
    if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return "—";
    if (totalSeconds < 60) return `${Math.round(totalSeconds)} sec`;
    const minutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = Math.round(totalSeconds % 60);
    if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m`;
};

function BreakdownPeriodSelect({ id, value, onChange }) {
    return (
        <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id={id}>Period</InputLabel>
            <Select
                labelId={id}
                label="Period"
                value={value}
                onChange={(event) => onChange(event.target.value)}
            >
                {BREAKDOWN_PERIOD_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                        {option.label}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}

function ExcludeAccountSwitches({ excludeTest, onExcludeTest, excludeStaff, onExcludeStaff }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <FormControlLabel
                sx={{ mr: 1 }}
                control={
                    <Switch
                        checked={excludeTest}
                        onChange={(event) => onExcludeTest(event.target.checked)}
                    />
                }
                label="Exclude test accounts"
            />
            <FormControlLabel
                sx={{ mr: 0 }}
                control={
                    <Switch
                        checked={excludeStaff}
                        onChange={(event) => onExcludeStaff(event.target.checked)}
                    />
                }
                label="Exclude staff"
            />
        </Box>
    );
}

/**
 * Player activity for puzzle types whose plays are one row per board per player (jigsaw, sliding).
 * `fetchStats` receives `{ start, end, language, includeTest, excludeStaff }` and must resolve the
 * `/v1/stats/{type}/user-plays` payload.
 */
export default function PuzzlePlayUserStatsPanel({
    language,
    fetchStats,
    rangeStorageKey,
    excludeTestStorageKey,
    excludeStaffStorageKey,
    difficultyChartTitle = "Board sizes played",
    difficultyChartHint = "Plays grouped by board size and difficulty.",
    notificationAppLabel = "this app",
    csvFilePrefix = "puzzle-play",
}) {
    const breakdownStorageKey = breakdownPeriodStorageKey(rangeStorageKey);

    const readStoredRange = useCallback(() => {
        try {
            const stored = window.localStorage.getItem(rangeStorageKey);
            return RANGE_OPTIONS.some((option) => option.value === stored) ? stored : DEFAULT_RANGE;
        } catch {
            return DEFAULT_RANGE;
        }
    }, [rangeStorageKey]);

    const readStoredBreakdownPeriod = useCallback(() => {
        if (!breakdownStorageKey) return DEFAULT_BREAKDOWN_PERIOD;
        try {
            const stored = window.localStorage.getItem(breakdownStorageKey);
            return BREAKDOWN_PERIOD_OPTIONS.some((option) => option.value === stored)
                ? stored
                : DEFAULT_BREAKDOWN_PERIOD;
        } catch {
            return DEFAULT_BREAKDOWN_PERIOD;
        }
    }, [breakdownStorageKey]);

    const breakdownExcludeTestKey = excludeTestStorageKey ? `${excludeTestStorageKey}.sizes` : "";
    const breakdownExcludeStaffKey = excludeStaffStorageKey ? `${excludeStaffStorageKey}.sizes` : "";

    const [range, setRange] = useState(readStoredRange);
    const [breakdownPeriod, setBreakdownPeriod] = useState(readStoredBreakdownPeriod);
    const [excludeTest, setExcludeTest] = useState(() => loadStoredBool(excludeTestStorageKey, true));
    const [excludeStaff, setExcludeStaff] = useState(() => loadStoredBool(excludeStaffStorageKey, true));
    const [sizeExcludeTest, setSizeExcludeTest] = useState(() => loadStoredBool(breakdownExcludeTestKey, true));
    const [sizeExcludeStaff, setSizeExcludeStaff] = useState(() => loadStoredBool(breakdownExcludeStaffKey, true));
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState(null);
    const [breakdownLoading, setBreakdownLoading] = useState(true);
    const [breakdownError, setBreakdownError] = useState(null);
    const [breakdownStats, setBreakdownStats] = useState(null);

    const handleRangeChange = useCallback(
        (value) => {
            setRange(value);
            try {
                window.localStorage.setItem(rangeStorageKey, value);
            } catch {
                /* private mode or storage disabled — keep the in-memory selection */
            }
        },
        [rangeStorageKey]
    );

    const handleBreakdownPeriodChange = useCallback(
        (value) => {
            setBreakdownPeriod(value);
            if (!breakdownStorageKey) return;
            try {
                window.localStorage.setItem(breakdownStorageKey, value);
            } catch {
                /* private mode or storage disabled — keep the in-memory selection */
            }
        },
        [breakdownStorageKey]
    );

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const end = dayjs();
                const start = end.subtract(rangeDays(range), "day").startOf("day");
                const result = await fetchStats({
                    start: start.unix(),
                    end: end.unix(),
                    language,
                    includeTest: !excludeTest,
                    excludeStaff,
                });
                if (!cancelled) {
                    setStats(result ?? null);
                }
            } catch (err) {
                Logger.error("Failed to load puzzle user stats:", err);
                if (!cancelled) {
                    setError("Failed to load user statistics.");
                    setStats(null);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [fetchStats, range, language, excludeTest, excludeStaff]);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setBreakdownLoading(true);
            setBreakdownError(null);
            try {
                const { start, end } = breakdownPeriodBounds(breakdownPeriod);
                const result = await fetchStats({
                    start: start.unix(),
                    end: end.unix(),
                    language,
                    includeTest: !sizeExcludeTest,
                    excludeStaff: sizeExcludeStaff,
                });
                if (!cancelled) {
                    setBreakdownStats(result ?? null);
                }
            } catch (err) {
                Logger.error("Failed to load puzzle breakdown stats:", err);
                if (!cancelled) {
                    setBreakdownError("Failed to load puzzle size and most-played stats.");
                    setBreakdownStats(null);
                }
            } finally {
                if (!cancelled) {
                    setBreakdownLoading(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [fetchStats, breakdownPeriod, language, sizeExcludeTest, sizeExcludeStaff]);

    const totals = stats?.totals ?? {};
    const daily = useMemo(() => (Array.isArray(stats?.daily) ? stats.daily : []), [stats]);
    const byDifficulty = useMemo(
        () => (Array.isArray(breakdownStats?.byDifficulty) ? breakdownStats.byDifficulty : []),
        [breakdownStats],
    );
    const topPuzzles = useMemo(
        () => (Array.isArray(breakdownStats?.topPuzzles) ? breakdownStats.topPuzzles : []),
        [breakdownStats],
    );
    const dailyLabels = useMemo(() => daily.map((row) => dayjs(row.date).format("MMM D")), [daily]);

    const hasPlays = Number(totals.plays ?? 0) > 0;

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 2, pt: 1 }}>
                <FormControl size="small" sx={{ minWidth: 170 }}>
                    <InputLabel id="puzzle-user-stats-range-label" shrink>
                        Duration
                    </InputLabel>
                    <Select
                        labelId="puzzle-user-stats-range-label"
                        label="Duration"
                        value={range}
                        onChange={(event) => handleRangeChange(event.target.value)}
                    >
                        {RANGE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <ExcludeAccountSwitches
                    excludeTest={excludeTest}
                    excludeStaff={excludeStaff}
                    onExcludeTest={(checked) => {
                        setExcludeTest(checked);
                        persistStoredBool(excludeTestStorageKey, checked);
                    }}
                    onExcludeStaff={(checked) => {
                        setExcludeStaff(checked);
                        persistStoredBool(excludeStaffStorageKey, checked);
                    }}
                />

                <Typography variant="body2" color="text.secondary">
                    {language} · signed-in players and guest sessions
                </Typography>
            </Box>

            {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {!loading && !error && stats && (
                <>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                            gap: 1,
                            mb: 3,
                        }}
                    >
                        <StatsSummaryTile label="Signed-in players" value={formatNumber(totals.players)} hint="Distinct accounts" />
                        <StatsSummaryTile
                            label="Android"
                            value={formatNumber(totals.playersAndroid)}
                            hint={`Signed-in · ${notificationAppLabel}`}
                        />
                        <StatsSummaryTile
                            label="Apple"
                            value={formatNumber(totals.playersApple)}
                            hint={`Signed-in · ${notificationAppLabel}`}
                        />
                        <StatsSummaryTile
                            label="Other / web"
                            value={formatNumber(totals.playersOther)}
                            hint="No Android or Apple device"
                        />
                        <StatsSummaryTile label="Guests" value={formatNumber(totals.guests)} hint="Distinct sessions, not signed in" />
                        <StatsSummaryTile
                            label="Finished a puzzle"
                            value={`${formatNumber(totals.playersCompleted)} / ${formatNumber(totals.guestsCompleted)}`}
                            hint="Players / guests with a completion"
                        />
                        <StatsSummaryTile label="Plays" value={formatNumber(totals.plays)} hint={`${formatNumber(totals.guestPlays)} by guests`} />
                        <StatsSummaryTile label="Completed plays" value={formatNumber(totals.completedPlays)} hint={formatPercent(totals.completionRate)} />
                        <StatsSummaryTile label="Plays per player" value={formatDecimal(totals.playsPerPlayer)} hint="Players and guests" />
                        <StatsSummaryTile
                            label="Average solve time"
                            value={formatDuration(totals.averageDurationSeconds)}
                            hint={`${formatNumber(totals.timedCompletedPlays)} timed completions`}
                        />
                        <StatsSummaryTile
                            label="Longest solve time"
                            value={formatDuration(totals.maxDurationSeconds)}
                            hint="Completed puzzles"
                        />
                        <StatsSummaryTile
                            label="Average attempts"
                            value={formatDecimal(totals.averageAttempts)}
                            hint="Times a board was opened"
                        />
                    </Box>

                    {!hasPlays && (
                        <Alert severity="info" sx={{ mb: 3 }}>
                            No plays recorded in this period for {language}
                            {excludeTest ? " (test accounts excluded)" : ""}
                            {excludeStaff ? " (staff excluded)" : ""}.
                        </Alert>
                    )}

                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: "primary.main" }}>
                                    Players per day
                                </Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                    <ExcludeAccountSwitches
                                        excludeTest={excludeTest}
                                        excludeStaff={excludeStaff}
                                        onExcludeTest={(checked) => {
                                            setExcludeTest(checked);
                                            persistStoredBool(excludeTestStorageKey, checked);
                                        }}
                                        onExcludeStaff={(checked) => {
                                            setExcludeStaff(checked);
                                            persistStoredBool(excludeStaffStorageKey, checked);
                                        }}
                                    />
                                    <CsvDownloadButton
                                        label="Download Players per day CSV"
                                        disabled={daily.length === 0}
                                        onClick={() =>
                                            downloadCsv(
                                                `${csvFilePrefix}-players-per-day.csv`,
                                                [
                                                    "date",
                                                    "players",
                                                    "playersAndroid",
                                                    "playersApple",
                                                    "playersOther",
                                                    "guests",
                                                    "plays",
                                                    "guestPlays",
                                                    "completedPlays",
                                                    "notifications",
                                                ],
                                                daily.map((row) => [
                                                    row.date ?? "",
                                                    csvNumber(row.players),
                                                    csvNumber(row.playersAndroid),
                                                    csvNumber(row.playersApple),
                                                    csvNumber(row.playersOther),
                                                    csvNumber(row.guests),
                                                    csvNumber(row.plays),
                                                    csvNumber(row.guestPlays),
                                                    csvNumber(row.completedPlays),
                                                    csvNumber(row.notifications),
                                                ]),
                                            )
                                        }
                                    />
                                </Box>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Distinct signed-in players split by platform (Android / Apple from{" "}
                                {notificationAppLabel} push tokens, falling back to signup source). Other is
                                web or unknown. Guests have no platform. The notification series marks{" "}
                                {notificationAppLabel} pushes sent that day (admin broadcast and puzzle publish).
                            </Typography>
                            {daily.length > 0 ? (
                                <LineChart
                                    height={300}
                                    xAxis={[{ scaleType: "point", data: dailyLabels }]}
                                    yAxis={[
                                        { id: "activity", min: 0 },
                                        { id: "notifications", min: 0, position: "right" },
                                    ]}
                                    series={[
                                        {
                                            data: daily.map((row) => Number(row.playersAndroid) || 0),
                                            label: "Android",
                                            color: "#3DDC84",
                                            showMark: daily.length <= 40,
                                            yAxisId: "activity",
                                        },
                                        {
                                            data: daily.map((row) => Number(row.playersApple) || 0),
                                            label: "Apple",
                                            color: "#555555",
                                            showMark: daily.length <= 40,
                                            yAxisId: "activity",
                                        },
                                        {
                                            data: daily.map((row) => Number(row.playersOther) || 0),
                                            label: "Other signed-in",
                                            color: "#3BA666",
                                            showMark: daily.length <= 40,
                                            yAxisId: "activity",
                                        },
                                        {
                                            data: daily.map((row) => Number(row.guests) || 0),
                                            label: "Guests",
                                            color: "#f0a202",
                                            showMark: daily.length <= 40,
                                            yAxisId: "activity",
                                        },
                                        {
                                            data: daily.map((row) => Number(row.plays) || 0),
                                            label: "Plays",
                                            color: "#1976d2",
                                            showMark: daily.length <= 40,
                                            yAxisId: "activity",
                                        },
                                        {
                                            data: daily.map((row) => {
                                                const count = Number(row.notifications) || 0;
                                                return count > 0 ? count : null;
                                            }),
                                            label: "Notifications sent",
                                            color: "#c62828",
                                            showMark: true,
                                            connectNulls: false,
                                            yAxisId: "notifications",
                                        },
                                    ]}
                                />
                            ) : (
                                <Alert severity="info">No daily activity in this period.</Alert>
                            )}
                        </CardContent>
                    </Card>

                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: "primary.main" }}>
                                    {difficultyChartTitle}
                                </Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                    <ExcludeAccountSwitches
                                        excludeTest={sizeExcludeTest}
                                        excludeStaff={sizeExcludeStaff}
                                        onExcludeTest={(checked) => {
                                            setSizeExcludeTest(checked);
                                            persistStoredBool(breakdownExcludeTestKey, checked);
                                        }}
                                        onExcludeStaff={(checked) => {
                                            setSizeExcludeStaff(checked);
                                            persistStoredBool(breakdownExcludeStaffKey, checked);
                                        }}
                                    />
                                    <BreakdownPeriodSelect
                                        id="puzzle-size-period-label"
                                        value={breakdownPeriod}
                                        onChange={handleBreakdownPeriodChange}
                                    />
                                    <CsvDownloadButton
                                        label={`Download ${difficultyChartTitle} CSV`}
                                        disabled={breakdownLoading || byDifficulty.length === 0}
                                        onClick={() =>
                                            downloadCsv(
                                                `${csvFilePrefix}-sizes-played.csv`,
                                                [
                                                    "band",
                                                    "plays",
                                                    "completedPlays",
                                                    "players",
                                                    "guests",
                                                ],
                                                byDifficulty.map((row) => [
                                                    row.band ?? "",
                                                    csvNumber(row.plays),
                                                    csvNumber(row.completedPlays),
                                                    csvNumber(row.players),
                                                    csvNumber(row.guests),
                                                ]),
                                            )
                                        }
                                    />
                                </Box>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {difficultyChartHint} This week is Monday through today.
                            </Typography>
                            {breakdownLoading ? (
                                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                                    <CircularProgress size={28} />
                                </Box>
                            ) : breakdownError ? (
                                <Alert severity="error">{breakdownError}</Alert>
                            ) : byDifficulty.length > 0 ? (
                                <BarChart
                                    height={320}
                                    xAxis={[{ scaleType: "band", data: byDifficulty.map((row) => row.band) }]}
                                    series={[
                                        {
                                            data: byDifficulty.map((row) => Number(row.plays) || 0),
                                            label: "Plays",
                                            color: "#1976d2",
                                        },
                                        {
                                            data: byDifficulty.map((row) => Number(row.completedPlays) || 0),
                                            label: "Completed",
                                            color: "#3BA666",
                                        },
                                        {
                                            data: byDifficulty.map((row) => Number(row.players) || 0),
                                            label: "Signed-in players",
                                            color: "#7b1fa2",
                                        },
                                        {
                                            data: byDifficulty.map((row) => Number(row.guests) || 0),
                                            label: "Guests",
                                            color: "#f0a202",
                                        },
                                    ]}
                                />
                            ) : (
                                <Alert severity="info">No plays to group by board size in this period.</Alert>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent>
                            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: "primary.main" }}>
                                    Most played puzzles
                                </Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                    <BreakdownPeriodSelect
                                        id="most-played-period-label"
                                        value={breakdownPeriod}
                                        onChange={handleBreakdownPeriodChange}
                                    />
                                    <CsvDownloadButton
                                        label="Download Most played puzzles CSV"
                                        disabled={breakdownLoading || topPuzzles.length === 0}
                                        onClick={() =>
                                            downloadCsv(
                                                `${csvFilePrefix}-most-played-puzzles.csv`,
                                                [
                                                    "rank",
                                                    "title",
                                                    "storyId",
                                                    "plays",
                                                    "completedPlays",
                                                    "players",
                                                    "guests",
                                                ],
                                                topPuzzles.map((row, index) => [
                                                    csvNumber(index + 1),
                                                    row.title ?? row.storyTitle ?? "",
                                                    row.storyId ?? row.id ?? "",
                                                    csvNumber(row.plays),
                                                    csvNumber(row.completedPlays),
                                                    csvNumber(row.players),
                                                    csvNumber(row.guests),
                                                ]),
                                            )
                                        }
                                    />
                                </Box>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                The busiest stories of the selected period, by play count.
                            </Typography>
                            {breakdownLoading ? (
                                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                                    <CircularProgress size={28} />
                                </Box>
                            ) : breakdownError ? (
                                <Alert severity="error">{breakdownError}</Alert>
                            ) : topPuzzles.length > 0 ? (
                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
                                        gap: 2,
                                        alignItems: "start",
                                    }}
                                >
                                    <MostPlayedTable rows={topPuzzles.slice(0, 5)} startRank={1} />
                                    <MostPlayedTable rows={topPuzzles.slice(5, 10)} startRank={6} />
                                </Box>
                            ) : (
                                <Alert severity="info">No plays in this period.</Alert>
                            )}
                        </CardContent>
                    </Card>
                </>
            )}
        </Box>
    );
}
